import {
	addWeeks,
	endOfWeek,
	format,
	isSameDay,
	isThisWeek,
	isTomorrow,
	isWithinInterval,
	isYesterday,
	startOfWeek,
} from "date-fns";

import {
	campaignHoldOf,
	findNextOutboxStep,
	INVITE_ACCEPTANCE_GATE_STEP_ID,
	isWaitingOutboxStep,
	MANUAL_MESSAGE_STEP_ID,
} from "@signalist/shared-types";

import { AssignStatus } from "@/enum/list.enum";
import { CampaignStatus } from "@/enum/campaign-status.enum";
import { isSendStep } from "@/utils/isSendStep";

import { ListPerson } from "../../../List/schema/list";

/**
 * The categories `getCurrentCategory` returns as a word rather than a date. Each one has its own
 * header label in `getDividerContent`; a category is never interpolated into a sentence, because
 * that is how the internal key "assigned" reached customers as "Scheduled for assigned" (PREC-4237).
 */
export const SCHEDULED_WORD_CATEGORY = {
	TODAY: "today",
	TOMORROW: "tomorrow",
	NEXT_WEEK: "next week",
	LATER: "later",
	/** ASSIGNED with no dated step yet — e.g. a first-step condition that has not been evaluated. */
	ASSIGNED: "assigned",
} as const;

/** The date format of a this-week scheduled category (e.g. "September 25, 2026"). */
export const SCHEDULED_DATE_CATEGORY_FORMAT = "MMMM d, yyyy";

/**
 * Outbox row grouping helpers. These mirror the backend `buildSortGroupSelect` CASE so the
 * client-side dividers/sort match the paginated order the API returns (PREC-3249). Kept in a
 * dedicated module so the categorization is independently unit-testable.
 */

/** The most recent send time for a lead (done send step → past conversation → lastConversationAt). */
export const getLastSentDate = (listPerson: ListPerson): string | null | undefined => {
	const lastConvo = listPerson.lastConversationAt;

	return (
		listPerson?.schedules
			?.slice()
			?.reverse()
			?.find((schedule) => schedule?.processingState === "done" && isSendStep(schedule))
			?.processedAt ??
		listPerson?.prospect?.conversations
			?.slice()
			?.reverse()
			?.find(
				(c: { sentAt: string | null; listPersonId?: number | null }) =>
					c?.sentAt && c?.listPersonId === listPerson.id
			)?.sentAt ??
		(lastConvo instanceof Date ? lastConvo.toISOString() : lastConvo) ??
		null
	);
};

/**
 * The send time of the lead's next step: the earliest still-pending (active, unsent) scheduled
 * time. Which step that is is decided by `findNextOutboxStep` in `@signalist/shared-types`, the
 * one rule the MCP `get_outbox` tool reports as the lead's next step too (PREC-4372).
 */
export const getPendingScheduleDate = (listPerson: ListPerson): string | null | undefined =>
	findNextOutboxStep(listPerson?.schedules)?.scheduledFor;

/** Maps a send timestamp to a "sent-*" divider bucket. */
export const getSentCategory = (sentDateStr: string): string => {
	const sentDateObj = new Date(sentDateStr);

	if (isSameDay(new Date(), sentDateObj)) {
		return "sent-today";
	}

	if (isYesterday(sentDateObj)) {
		return "sent-yesterday";
	}

	return `sent-${format(sentDateObj, "yyyy-MM-dd")}`;
};

/** Maps a future scheduled timestamp to a "scheduled" divider bucket (today/tomorrow/week/later). */
export const getScheduledCategory = (scheduledFor: string): string => {
	const dateObj = new Date(scheduledFor);

	if (isSameDay(new Date(), dateObj)) {
		return SCHEDULED_WORD_CATEGORY.TODAY;
	}

	if (isTomorrow(dateObj)) {
		return SCHEDULED_WORD_CATEGORY.TOMORROW;
	}

	if (isThisWeek(dateObj)) {
		return format(dateObj, SCHEDULED_DATE_CATEGORY_FORMAT);
	}

	const nextWeekStart = startOfWeek(addWeeks(new Date(), 1));
	const nextWeekEnd = endOfWeek(addWeeks(new Date(), 1));

	if (isWithinInterval(dateObj, { start: nextWeekStart, end: nextWeekEnd })) {
		return SCHEDULED_WORD_CATEGORY.NEXT_WEEK;
	}

	return SCHEDULED_WORD_CATEGORY.LATER;
};

/**
 * A pending manual message (stepId `n99`) the lead will still send: switched on, dated, not sent,
 * cancelled or failed. The publisher takes such a row whatever its thread's campaign does
 * (`followUpSchedules` in `apps/api/src/schedule/repositories/schedule-publishing.repository.ts`),
 * so a paused campaign does not hold it. "Pending and will go out" is the shared
 * `isWaitingOutboxStep`, the same rule the card's time (`getPendingScheduleDate`) and the MCP
 * `get_outbox` read.
 *
 * Twin: the API's `buildOutboxPausedSql` and its `buildPendingManualMessageExistsSql`
 * (`apps/api/src/list/helpers/outbox-tab-conditions.helper.ts`); each side has a spec pinning the
 * same cases.
 */
export const hasPendingManualMessage = (listPerson: ListPerson): boolean =>
	!!listPerson.schedules?.some(
		// A failed manual message is not pending: the API's predicate requires `failedAt IS NULL`.
		(s) => s.stepId === MANUAL_MESSAGE_STEP_ID && !s.failedAt && isWaitingOutboxStep(s)
	);

/**
 * Whether a paused campaign holds this lead's Outbox item: its list rollup is PAUSED or one of its
 * steps belongs to a PAUSED campaign — unless a pending manual message goes out anyway (PREC-4559).
 * Twin of the API's `buildOutboxPausedSql`; the publisher's manual-message path is the authority.
 */
export const isHeldByPausedCampaign = (listPerson: ListPerson): boolean => {
	const pausedCampaign =
		!!listPerson.schedules?.some((s) => s.campaignStatus === CampaignStatus.PAUSED) ||
		listPerson.list.campaignStatus === CampaignStatus.PAUSED;

	return pausedCampaign && !hasPendingManualMessage(listPerson);
};

/**
 * Classifies a lead into its Outbox divider bucket. Mirrors backend `buildSortGroupSelect` CASE
 * order exactly. Per-campaign authority (PREC-3249): a campaign step "will fire" only while its OWN
 * campaign runs — the shared `campaignHoldOf`, which the card's time (`getPendingScheduleDate`) and
 * the MCP `get_outbox` read too, so card, divider and agent agree (PREC-4372). A step of a paused,
 * never started or ended campaign is not "scheduled", even when the list rollup
 * (`list.campaignStatus`) is still RUNNING because a sibling campaign on the same list runs; a lead
 * held by a paused one is grouped as "paused". A manual message goes out whatever its thread's
 * campaign does, as the backend's `stepId = 'n99'` arm says.
 */
export const getCurrentCategory = (
	listPerson: ListPerson,
	todayStart: Date
): string | null | undefined => {
	const isWillFireSchedule = (s: NonNullable<ListPerson["schedules"]>[number]) =>
		s.active &&
		s.scheduledFor &&
		!s.processedAt &&
		!s.canceledAt &&
		// The invite-acceptance gate never sends; it only watches for the acceptance (30 days), so a
		// lead waiting on it is not "scheduled". Kept in lockstep with buildSortGroupCaseExpression.
		s.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID &&
		campaignHoldOf(s) === null &&
		new Date(s.scheduledFor) >= todayStart;

	// sortGroup 5: a genuinely held (paused) schedule — active = false but still a pending hold. A
	// cancelled draft is `active = false` too (the per-bubble cancel clears the flag), so without
	// these guards a follow-up the user deleted while editing kept the lead pinned under "paused"
	// in the Outbox (PREC-4529). Mirrors the API's buildOutboxPausedSql inactive-schedule arm;
	// `publishedAt` is deliberately not read (a campaign step paused after publish keeps it).
	const hasInactiveSchedule = listPerson.schedules?.some(
		(s) => !s.active && !s.canceledAt && !s.processedAt && !s.failedAt
	);

	// sortGroup 0: has active future schedule whose own campaign will fire
	const hasFutureActiveSchedule = listPerson.schedules?.some(isWillFireSchedule);
	const pendingManualMessage = hasPendingManualMessage(listPerson);

	// A genuinely held schedule outranks this branch, as it does in the API's classifier (PREC-4559);
	// a pending manual message goes out whatever the rollup says, so a PAUSED rollup does not stop
	// its scheduled-date grouping either.
	if (
		!hasInactiveSchedule &&
		hasFutureActiveSchedule &&
		(pendingManualMessage || listPerson.list.campaignStatus !== CampaignStatus.PAUSED)
	) {
		const scheduledFor = listPerson.schedules
			?.filter(isWillFireSchedule)
			?.sort(
				(a, b) => new Date(a.scheduledFor!).getTime() - new Date(b.scheduledFor!).getTime()
			)?.[0]?.scheduledFor;

		if (scheduledFor) {
			return getScheduledCategory(scheduledFor);
		}

		return null;
	}

	// sortGroup 5: a genuinely held (paused) schedule keeps the lead paused.
	if (hasInactiveSchedule || listPerson.assignStatus === "PAUSED") {
		return "paused";
	}

	// sortGroup 1: assignStatus is ENDED, MESSAGE_SENT, or DONE
	if (["ENDED", "MESSAGE_SENT", "DONE"].includes(listPerson.assignStatus)) {
		// Pending schedule (e.g. manually sent follow-up) takes priority
		const pendingDate = getPendingScheduleDate(listPerson);

		if (pendingDate) {
			return getScheduledCategory(pendingDate);
		}

		const sentDate = getLastSentDate(listPerson);

		if (sentDate) {
			return getSentCategory(sentDate);
		}

		return "sent-unknown";
	}

	// sortGroup 5: a paused campaign holds the lead — unless a pending manual message goes out
	// anyway (PREC-4559).
	if (hasInactiveSchedule || isHeldByPausedCampaign(listPerson)) {
		return "paused";
	}

	// sortGroup 1: has a processed (not canceled) send step — a condition node sends nothing (PREC-4157)
	const hasProcessedSchedule = listPerson.schedules?.some(
		(s) => s.processedAt && !s.canceledAt && isSendStep(s)
	);

	if (hasProcessedSchedule || listPerson.lastConversationAt) {
		const sentDate = getLastSentDate(listPerson);

		if (sentDate) {
			return getSentCategory(sentDate);
		}
	}

	// A lead whose next step waits for a campaign that does not run (not started, no status row,
	// ended) has nothing sent and nothing about to send: it waits like an ASSIGNED lead (PREC-4372).
	const hasHeldOpenStep = listPerson.schedules?.some(
		(s) =>
			s.active &&
			s.scheduledFor &&
			!s.processedAt &&
			!s.canceledAt &&
			s.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID &&
			campaignHoldOf(s) !== null
	);

	if (listPerson.assignStatus === AssignStatus.ASSIGNED || hasHeldOpenStep) {
		return SCHEDULED_WORD_CATEGORY.ASSIGNED;
	}

	return "sent-unknown";
};
